import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Free gift auto-add/remove for the cart drawer rewards bar.
 *
 * Reads its config from the JSON script tag rendered by `snippets/cart-progress-bar.liquid`
 * (gift variant id, threshold in cents, section id). On every cart update it re-checks the
 * cart total (excluding the gift line itself) and adds/removes the gift accordingly.
 *
 * Session state prevents fighting the customer: if they manually remove the gift while
 * still eligible, we mark it dismissed and don't re-add until they drop below the
 * threshold and cross it again.
 *
 * Loaded globally from `snippets/gbk-free-gift.liquid` (via `gbk-theme`), not from
 * the cart drawer: the drawer only renders its rewards bar in the *non-empty* branch,
 * so with an empty cart at page load this file was not on the page, and the markup
 * that carries it arrives through a morph, which never executes a `<script>`. That is
 * why the gift used to appear only after a refresh.
 *
 * It also patches GoKwik's `window.onBuyNowClick` so a Buy-now that never touches the
 * cart still carries the gift when the line it checks out clears the threshold.
 */

const STATE_KEY_PREFIX = 'inmyo:free-gift-state';
const FAILURE_COOLDOWN = 15000;

let busy = false;
/**
 * A cart update that lands while a sync is in flight used to be dropped on the
 * floor (`if (busy) return`), which is one of the ways the gift silently failed
 * to appear: add two things quickly, and the update that finally crossed the
 * threshold was the one thrown away. Remember it instead and run once more.
 */
let rerun = false;
let debounceTimer = 0;
let cooldownUntil = 0;

/**
 * The dismissed/added flag is scoped to the configured gift and threshold, so
 * changing either in theme settings starts from a clean slate instead of
 * inheriting a "dismissed" flag from the previous configuration.
 * @param {{ variantId: number, threshold: number }} config
 */
const stateKey = (config) => `${STATE_KEY_PREFIX}:${config.variantId}:${config.threshold}`;

/** @param {{ variantId: number, threshold: number }} config */
const getState = (config) => sessionStorage.getItem(stateKey(config));

/**
 * @param {{ variantId: number, threshold: number }} config
 * @param {string | null} value
 */
const setState = (config, value) => {
  if (value) {
    sessionStorage.setItem(stateKey(config), value);
  } else {
    sessionStorage.removeItem(stateKey(config));
  }
};

function getConfig() {
  const el = document.querySelector('[data-cart-gift-config]');
  if (!el || !el.textContent) return null;

  try {
    const config = JSON.parse(el.textContent);
    if (!config.variantId || !config.threshold) return null;
    return config;
  } catch {
    return null;
  }
}

/**
 * Events we dispatch ourselves, tracked by identity so the listener below can
 * ignore them. Horizon 4.x's CartLinesUpdateEvent carries its `source` on the
 * resolved promise rather than on the event, so there is nothing to read
 * synchronously the way 3.x's CartUpdateEvent allowed — identity is exact and
 * needs no assumptions about ordering.
 * @type {WeakSet<Event>}
 */
const selfDispatched = new WeakSet();

/**
 * Tell the theme the cart changed, handing over the section HTML the cart API
 * already returned so cart-items-component morphs instead of refetching.
 *
 * `cart` must be a full cart object (the shape of `/cart.js` or `/cart/change.js`),
 * not the response of `/cart/add.js`. The add endpoint returns the added line
 * items, not a cart: it has no `currency`, and `createCartFromAjaxResponse`
 * formats money with `Intl.NumberFormat({ style: 'currency' })`, which throws
 * `Currency code is required with currency style` without it. That throw used to
 * escape before the event resolved, so the drawer never morphed and the gift only
 * appeared after a manual refresh.
 *
 * @param {Object} cart - A full cart object
 * @param {Object} detail - `sections` and `items` to hand to the morph
 * @param {'add' | 'remove'} action - What we did to the gift line
 * @param {Array<{ id: string | number, quantity: number }>} lines - The gift line
 */
function notifyCartChanged(cart, detail, action, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({
    action,
    context: 'cart',
    lines,
    promise: deferred.promise,
  });

  selfDispatched.add(event);
  document.dispatchEvent(event);

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      sections: detail.sections,
      items: detail.items,
      source: 'gbk-cart-free-gift',
    },
  });
}

/**
 * Read the cart, guaranteeing a fresh copy.
 *
 * `/cart.js` comes back with `cdn-cache-control: no-cache, no-store` but **no**
 * plain `cache-control`, so nothing tells the *browser* not to keep it. A
 * `fetch()` is then free to answer from the HTTP cache, and every decision here
 * is made from a cart that has already moved on: an add that crossed the
 * threshold looks like it never happened (the gift appears only after a manual
 * refresh), and a removal that dropped under it looks like the cart is still
 * eligible (the gift stays in a ₹922 cart). Both were reported, and both are
 * this. `no-store` opts out of the cache in both directions; the timestamp
 * makes the URL unique so nothing in front of the browser can answer either.
 */
const fetchCart = async () =>
  (await fetch(`${Theme.routes.cart_url}.js?_=${Date.now()}`, { cache: 'no-store' })).json();

/**
 * Which sections the cart API should re-render with the change.
 *
 * Read off the DOM rather than baked into the config, because the answer differs
 * per template: the drawer is `cart-drawer-section`, the cart page is whatever id
 * `templates/cart.json` gives its `main-cart` section, and on the cart page the
 * drawer is not rendered at all. `cart-items-component` carries the id it was
 * rendered for, in both places, so asking the page is always right.
 *
 * @param {{ sectionId: string }} config
 * @returns {string} Comma-separated section ids for the `sections` parameter
 */
function sectionsParam(config) {
  const ids = new Set();

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
    if (id) ids.add(id);
  });

  if (ids.size === 0 && config.sectionId) ids.add(config.sectionId);

  return [...ids].join(',');
}

/** @param {{ variantId: number, threshold: number, sectionId: string }} config */
async function addGift(config) {
  const body = JSON.stringify({
    items: [{ id: config.variantId, quantity: 1, properties: { _free_gift: 'true' } }],
    sections: sectionsParam(config),
  });

  const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
  const data = await response.json();

  // 422 from /cart/add.js: the variant is sold out, or the product is draft or
  // not published to the Online Store sales channel. Retrying every cart update
  // would just repeat it, so back off and say why once.
  if (data.status) {
    cooldownUntil = Date.now() + FAILURE_COOLDOWN;
    console.warn('[cart-free-gift] could not add gift', data.description || data.message || data);
    return false;
  }

  if (data.sections) {
    const cart = await fetchCart();
    notifyCartChanged(cart, { sections: data.sections, items: data.items }, 'add', [
      { id: config.variantId, quantity: 1 },
    ]);
  }
  return true;
}

/**
 * Set the gift line to a given quantity; 0 removes it.
 *
 * Unlike `/cart/add.js`, `/cart/change.js` answers with a full cart object, so
 * its response can go straight to `notifyCartChanged`.
 *
 * @param {{ sectionId: string }} config
 * @param {{ key: string }} giftLine
 * @param {number} quantity
 */
async function setGiftQuantity(config, giftLine, quantity) {
  const body = JSON.stringify({
    id: giftLine.key,
    quantity,
    sections: sectionsParam(config),
  });

  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  const data = await response.json();

  // `/cart/change.js` answers 4xx with a `status` field and no cart. Silence
  // here meant a gift that could not be removed simply stayed in the cart with
  // nothing said about it anywhere.
  if (data.status) {
    cooldownUntil = Date.now() + FAILURE_COOLDOWN;
    console.warn('[cart-free-gift] could not change gift line', data.description || data.message || data);
    return;
  }

  if (data.sections) {
    notifyCartChanged(data, { sections: data.sections, items: data.items }, quantity === 0 ? 'remove' : 'add', [
      { id: giftLine.key, quantity },
    ]);
  }
}

async function syncGift() {
  if (busy) {
    rerun = true;
    return;
  }
  // After a failed/challenged request, stay quiet for a while instead of
  // hammering the cart API on every subsequent update.
  if (Date.now() < cooldownUntil) return;

  const config = getConfig();
  if (!config) return;

  busy = true;

  try {
    const cart = await fetchCart();
    const giftLine = cart.items.find(
      (/** @type {{ variant_id: number }} */ item) => item.variant_id === config.variantId
    );
    // Mirror snippets/gbk-cart-progress-bar.liquid exactly: measure against the
    // pre-cart-discount subtotal, so an unlocked discount milestone (e.g. 10% off)
    // can't drop total_price back under the gift threshold and leave the bar
    // showing "unlocked" while this script considers the cart ineligible.
    const eligibleTotal = cart.items_subtotal_price - (giftLine ? giftLine.final_line_price : 0);
    const eligible = eligibleTotal >= config.threshold;

    if (eligible && giftLine) {
      // One gift per cart. The quantity stepper is hidden on the gift line, but a
      // stale tab, a restored cart, or a repeated add can still leave more than
      // one — pin it back to a single unit rather than giving away the surplus.
      if (giftLine.quantity > 1) {
        await setGiftQuantity(config, giftLine, 1);
      }
      setState(config, 'added');
    } else if (!eligible) {
      // Dropped below the threshold: remove the auto-added gift and reset state.
      //
      // The session flag alone is not enough to recognise our own line. It lives
      // in sessionStorage, so a second tab, a restored session or cleared storage
      // loses it while the gift is still sitting in the cart — and the gift then
      // stayed put below the threshold no matter how far the cart fell, which is
      // exactly the ₹792-with-a-free-gift case. The `_free_gift` property stamped
      // on at add time survives all of that, and still tells our line apart from
      // one the shopper deliberately added themselves (the gift is a real, if
      // hidden, ₹0 product), which stays untouched.
      const isOurs = getState(config) === 'added' || giftLine?.properties?._free_gift === 'true';

      if (giftLine && isOurs) {
        await setGiftQuantity(config, giftLine, 0);
      }
      setState(config, null);
    } else if (getState(config) === 'added') {
      // Eligible, gift missing, but we previously added it => customer removed it manually.
      setState(config, 'dismissed');
    } else if (getState(config) !== 'dismissed') {
      paintPending(config, true);
      try {
        const added = await addGift(config);
        if (added) setState(config, 'added');
      } finally {
        paintPending(config, false);
      }
    }
    paintDismissed(config, eligible && !giftLine && getState(config) === 'dismissed');
  } catch (error) {
    cooldownUntil = Date.now() + FAILURE_COOLDOWN;
    console.error('[cart-free-gift]', error);
  } finally {
    busy = false;
    if (rerun) {
      rerun = false;
      scheduleSync();
    }
  }
}

function scheduleSync() {
  clearTimeout(debounceTimer);
  // Short. This used to be 500ms to keep the read behind the theme's own cart
  // request, but that was guarding a staleness `fetchCart`'s `no-store` now
  // rules out, and the theme dispatches its event after its request resolves
  // anyway. Every millisecond here is one the shopper spends looking at an
  // unlocked rewards bar with no gift under it, so this buys nothing and costs
  // the thing people actually notice. Bursts are `rerun`'s job.
  debounceTimer = setTimeout(syncGift, 150);
}

/* ------------------------------------------------------------------ *
 * The dismissed notice
 *
 * Removing the gift by hand is respected for the rest of the session (see the
 * state machine in `syncGift`), but the rewards bar underneath is server
 * rendered and keeps saying "you've unlocked all rewards" — a cart over the
 * threshold with no gift in it and a bar claiming otherwise reads as a bug, and
 * was reported as one. So the bar is told, on the client, what actually
 * happened, and offers the gift back.
 *
 * The bar is re-rendered by a morph on every cart update, which takes this
 * button with it, so painting is idempotent and re-runs after each update.
 * ------------------------------------------------------------------ */

const RESTORE_CLASS = 'cart-rewards__restore';
const PENDING_CLASS = 'cart-rewards__pending';
const DISMISSED_CLASS = 'cart-rewards--gift-dismissed';

/** Last known answers, so a repaint after a morph needs no cart fetch. */
let lastDismissed = false;

/**
 * True from the moment we decide to add the gift until the drawer has it.
 *
 * Adding it is not instant and cannot be: the shopper's own add has to finish,
 * then the cart is read, then the gift is added asking for the drawer section
 * back, and rendering that section server-side is the slowest part of all.
 * Measured on the live store that is a few seconds, and for every one of them
 * the bar sat there saying "you've unlocked all rewards" above a cart with no
 * gift in it — which is indistinguishable from broken, and was reported as
 * needing a refresh twice. Saying what is happening costs nothing and is true.
 */
let lastPending = false;

/**
 * @param {{ variantId: number, threshold: number, sectionId: string }} config
 * @param {boolean} dismissed - Eligible, but the shopper took the gift out
 */
function paintPending(config, pending) {
  lastPending = pending;

  document.querySelectorAll('.cart-rewards').forEach((bar) => {
    const existing = bar.querySelector(`.${PENDING_CLASS}`);

    if (!pending) {
      existing?.remove();
      return;
    }

    if (existing) return;

    const note = document.createElement('p');
    note.className = PENDING_CLASS;
    note.setAttribute('role', 'status');
    note.textContent = 'Adding your free gift…';
    bar.append(note);
  });
}

/**
 * @param {{ variantId: number, threshold: number, sectionId: string }} config
 * @param {boolean} dismissed - Eligible, but the shopper took the gift out
 */
function paintDismissed(config, dismissed) {
  lastDismissed = dismissed;

  document.querySelectorAll('.cart-rewards').forEach((bar) => {
    const existing = bar.querySelector(`.${RESTORE_CLASS}`);

    if (!dismissed) {
      existing?.remove();
      bar.classList.remove(DISMISSED_CLASS);
      return;
    }

    bar.classList.add(DISMISSED_CLASS);
    if (existing) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = RESTORE_CLASS;
    button.textContent = 'Free gift removed — add it back';

    button.addEventListener('click', () => {
      button.disabled = true;
      button.textContent = 'Adding…';
      // Clearing the flag is all it takes: the sync sees an eligible cart with
      // no gift and nothing dismissing it, and adds it the ordinary way.
      setState(config, null);
      scheduleSync();
    });

    bar.append(button);
  });
}

/* ------------------------------------------------------------------ *
 * Buy now
 *
 * GoKwik's OCC claims the dynamic checkout button and calls the global
 * `onBuyNowClick(event, false, [{ id, quantity }])`, which opens a checkout for
 * exactly those line items — the cart is never involved, so nothing here ever
 * ran and a ₹999+ Buy-now went out without the gift.
 *
 * The fix is to hand that call a longer item list. `window.onBuyNowClick` is
 * replaced with an accessor so it does not matter whether the SDK defines it
 * before or after this module runs: whatever it assigns is kept as the target
 * and every read gets the wrapper.
 *
 * Prices have to be known *synchronously* at click time (the SDK's call is
 * synchronous), so they come from two sources filled in ahead of it: the
 * variant JSON `variant-picker` already renders for Horizon's own script, and a
 * one-off fetch of the product JSON on product pages, which also covers
 * products with only a default variant (those render no picker at all).
 * ------------------------------------------------------------------ */

/** @type {Map<number, number>} variant id → price in cents */
const priceCache = new Map();

/** Harvest the variant JSON the pickers on the page are already carrying. */
function readPricesFromDom() {
  document.querySelectorAll('variant-picker script[type="application/json"]').forEach((el) => {
    try {
      const variant = JSON.parse(el.textContent || '');
      if (variant && typeof variant.id === 'number' && typeof variant.price === 'number') {
        priceCache.set(variant.id, variant.price);
      }
    } catch {
      // A picker mid-morph can hold half a document; the next read gets it.
    }
  });
}

/** Pull every variant price for the product page we are on, if we are on one. */
async function prefetchProductPrices() {
  const match = window.location.pathname.match(/^.*\/products\/[^/?#]+/);
  if (!match) return;

  try {
    const response = await fetch(`${match[0]}.js`);
    if (!response.ok) return;

    const product = await response.json();
    for (const variant of product.variants || []) {
      if (typeof variant.id === 'number' && typeof variant.price === 'number') {
        priceCache.set(variant.id, variant.price);
      }
    }
  } catch {
    // Offline or a redirect: Buy-now simply goes out without the gift.
  }
}

/**
 * The line items to check out, with the gift appended when they clear the
 * threshold on their own.
 *
 * Returns `items` untouched whenever anything is unknown — a variant with no
 * cached price, the gift already present. Giving a gift away by guessing is
 * worse than not giving it, and the cart flow still catches the shopper who
 * adds to cart instead.
 *
 * @param {{ variantId: number, threshold: number }} config
 * @param {Array<{ id: string | number, quantity?: number }>} items
 */
function withGift(config, items) {
  if (!Array.isArray(items) || items.length === 0) return items;
  if (items.some((item) => Number(item.id) === config.variantId)) return items;

  readPricesFromDom();

  let total = 0;
  for (const item of items) {
    const price = priceCache.get(Number(item.id));
    if (price == null) return items;
    total += price * (Number(item.quantity) || 1);
  }

  if (total < config.threshold) return items;

  return [...items, { id: config.variantId, quantity: 1 }];
}

/** @param {{ variantId: number, threshold: number }} config */
function installBuyNowHook(config) {
  let target = /** @type {Function | undefined} */ (
    /** @type {any} */ (window).onBuyNowClick
  );

  /** @this {any} @param {any[]} args */
  function wrapper(...args) {
    try {
      if (Array.isArray(args[2])) args[2] = withGift(config, args[2]);
    } catch (error) {
      console.warn('[cart-free-gift] buy now', error);
    }
    return target?.apply(this, args);
  }

  try {
    Object.defineProperty(window, 'onBuyNowClick', {
      configurable: true,
      get: () => (typeof target === 'function' ? wrapper : target),
      set: (value) => {
        target = value;
      },
    });
  } catch (error) {
    console.warn('[cart-free-gift] could not hook buy now', error);
  }
}

/**
 * Keep the notice on a bar that keeps being rebuilt.
 *
 * The rewards bar is server-rendered markup inside the drawer, and every cart
 * update morphs a fresh copy in — which throws our button away, because the
 * server never renders it. Painting once after the sync therefore only sticks
 * if the morph happened to land first; when it landed second the button was
 * wiped and appeared only after a refresh, which is what was reported.
 *
 * So the paint is not a one-off. Whenever the DOM changes and we know the gift
 * is dismissed, the notice is re-applied. `paintDismissed` is idempotent — it
 * appends only when the button is missing — so re-entry from our own insertion
 * settles on the next frame instead of looping.
 *
 * @param {{ variantId: number, threshold: number, sectionId: string }} config
 */
function watchForRepaint(config) {
  let queued = false;

  const observer = new MutationObserver(() => {
    if (queued) return;
    if (!lastDismissed && !lastPending) return;
    if (lastDismissed && document.querySelector(`.${RESTORE_CLASS}`)) return;
    if (lastPending && document.querySelector(`.${PENDING_CLASS}`)) return;

    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      paintDismissed(config, lastDismissed);
      paintPending(config, lastPending);
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

const giftConfig = getConfig();
if (giftConfig) {
  installBuyNowHook(giftConfig);
  prefetchProductPrices();
  watchForRepaint(giftConfig);
}

/**
 * A second look, a couple of seconds after the cart settles.
 *
 * The belt to the braces below. Not every path that changes the cart carries a
 * promise to wait on, and a decision made from a cart read at the wrong moment
 * sticks forever, because nothing else will fire: the shopper has stopped
 * touching the cart, and the sync only ever runs on events. One extra read per
 * cart change is a small price for a state that cannot get permanently stuck.
 */
let verifyTimer = 0;

function scheduleVerify() {
  clearTimeout(verifyTimer);
  verifyTimer = setTimeout(syncGift, 2000);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  // Our own add/remove already dispatched this event; re-syncing would only
  // refetch the cart for nothing and add to the request volume. The morph that
  // follows still wipes the notice off the bar, so repaint it once the DOM has
  // settled — with the answer the last sync worked out, not a fresh fetch.
  if (selfDispatched.has(event)) {
    if (giftConfig) setTimeout(() => paintDismissed(giftConfig, lastDismissed), 250);
    return;
  }

  // Wait for the cart request, do not guess at it.
  //
  // component-cart-items.js dispatches this event *before* it sends anything —
  // the event carries a `promise` that resolves once the response is in, and
  // Horizon's own listeners (see cart-icon.js) read the cart only after it.
  // Syncing on a timer instead meant reading the cart as it was *before* the
  // shopper's change: a cart that had just crossed the threshold still looked
  // short of it, the sync concluded "not eligible", wrote that state and went
  // quiet, and the gift never arrived until the page was loaded again. The
  // shorter the delay the likelier that was, which is why it got worse rather
  // than better when the debounce came down.
  const promise = /** @type {{ promise?: Promise<unknown> }} */ (
    /** @type {unknown} */ (event)
  ).promise;

  if (typeof promise?.then === 'function') {
    promise.then(scheduleSync, scheduleSync);
  } else {
    scheduleSync();
  }

  scheduleVerify();
});
scheduleSync();
